import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useProject } from "./useProject";
import { toast } from "sonner";
import {
  VariableMapping,
  VariableMappingFormData,
} from "@/types/variable-mappings";

export function useVariableMappings(templateId: number | null) {
  const { projectId } = useProject();
  const queryClient = useQueryClient();

  // Query to get all mappings for a template
  const mappingsQuery = useQuery({
    queryKey: ["variable-mappings", templateId, projectId],
    queryFn: async () => {
      if (!templateId || !projectId) {
        return [];
      }

      const { data, error } = await (supabase as any)
        .from("template_variable_mappings")
        .select(`
          *,
          question:question_id (
            id,
            name,
            enunciado
          )
        `)
        .eq("template_id", templateId)
        .eq("project_id", projectId)
        .order("variable_name");

      if (error) {
        console.error("Error fetching variable mappings:", error);
        throw error;
      }

      // Transform the data to match expected interface
      const mappings = (data || []).map((mapping: any) => ({
        ...mapping,
        question: mapping.question ? {
          id: mapping.question.id,
          name: mapping.question.name,
          text: mapping.question.enunciado || mapping.question.name,
        } : undefined
      }));

      return mappings as VariableMapping[];
    },
    enabled: !!templateId && !!projectId,
  });

  // Mutation to create or update a mapping
  const saveMappingMutation = useMutation({
    mutationFn: async (data: {
      id?: number;
      formData: VariableMappingFormData;
    }) => {
      if (!templateId || !projectId) {
        throw new Error("Template ID and Project ID are required");
      }

      const mappingData = {
        project_id: projectId,
        template_id: templateId,
        variable_name: data.formData.variable_name,
        variable_placeholder: data.formData.variable_placeholder,
        source_type: data.formData.source_type,
        question_id: data.formData.question_id,
        question_name: data.formData.question_name,
        question_group: data.formData.question_group,
        form_source: data.formData.form_source || 'main',
        matrix_row_id: data.formData.matrix_row_id,
        matrix_row_text: data.formData.matrix_row_text,
        matrix_column_id: data.formData.matrix_column_id,
        matrix_column_text: data.formData.matrix_column_text,
        calculation_type: data.formData.calculation_type,
        calculation_config: data.formData.calculation_config,
        description: data.formData.description,
        default_value: data.formData.default_value,
      };

      if (data.id) {
        // Update existing
        const { data: result, error } = await (supabase as any)
          .from("template_variable_mappings")
          .update(mappingData as any)
          .eq("id", data.id)
          .select()
          .single();

        if (error) throw error;
        return result;
      } else {
        // Insert new
        const { data: result, error } = await (supabase as any)
          .from("template_variable_mappings")
          .insert(mappingData as any)
          .select()
          .single();

        if (error) throw error;
        return result;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["variable-mappings", templateId, projectId],
      });
      toast.success("Mapeo de variable guardado exitosamente");
    },
    onError: (error: any) => {
      console.error("Error saving variable mapping:", error);
      toast.error(error.message || "Error al guardar el mapeo");
    },
  });

  // Mutation to delete a mapping
  const deleteMappingMutation = useMutation({
    mutationFn: async (mappingId: number) => {
      const { error } = await supabase
        .from("template_variable_mappings")
        .delete()
        .eq("id", mappingId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["variable-mappings", templateId, projectId],
      });
      toast.success("Mapeo eliminado exitosamente");
    },
    onError: (error: any) => {
      console.error("Error deleting variable mapping:", error);
      toast.error(error.message || "Error al eliminar el mapeo");
    },
  });

  // Helper to check if a variable is already mapped
  const isVariableMapped = (variableName: string): boolean => {
    return (
      mappingsQuery.data?.some((m) => m.variable_name === variableName) || false
    );
  };

  // Helper to get mapping for a variable
  const getMappingForVariable = (
    variableName: string
  ): VariableMapping | undefined => {
    return mappingsQuery.data?.find((m) => m.variable_name === variableName);
  };

  return {
    mappings: mappingsQuery.data || [],
    isLoading: mappingsQuery.isLoading,
    error: mappingsQuery.error,
    saveMapping: saveMappingMutation.mutateAsync,
    isSaving: saveMappingMutation.isPending,
    deleteMapping: deleteMappingMutation.mutateAsync,
    isDeleting: deleteMappingMutation.isPending,
    isVariableMapped,
    getMappingForVariable,
    refetch: mappingsQuery.refetch,
  };
}

// Hook to get mapping for a specific variable
export function useVariableMapping(
  templateId: number | null,
  variableName: string | null
) {
  const { projectId } = useProject();

  return useQuery({
    queryKey: ["variable-mapping", templateId, variableName, projectId],
    queryFn: async () => {
      if (!templateId || !variableName || !projectId) {
        return null;
      }

      const { data, error } = await (supabase as any)
        .from("template_variable_mappings")
        .select(`
          *,
          question:question_id (
            id,
            name,
            enunciado
          )
        `)
        .eq("template_id", templateId)
        .eq("variable_name", variableName)
        .eq("project_id", projectId)
        .maybeSingle();

      if (error) {
        console.error("Error fetching variable mapping:", error);
        throw error;
      }

      if (!data) return null;

      // Transform the data to match expected interface
      return {
        ...data,
        question: data.question ? {
          id: data.question.id,
          name: data.question.name,
          text: data.question.enunciado || data.question.name,
        } : undefined
      } as VariableMapping;
    },
    enabled: !!templateId && !!variableName && !!projectId,
  });
}
